/* eslint-disable no-param-reassign */

import _ from 'underscore';
import { uncheckedInjectionContext as globalContext } from '@bingads-webui-campaign/injection-context';
import { BackboneSchema as backboneSchema } from '@bingads-webui/backbone-schema';
import { InputControl } from '@bingads-webui/input-control';
import { PercentageBid, FixedBid } from '@bingads-webui/hotel-odata-schemas';
import { BulkEditPanelBase } from './bulk-edit-panel-base';
import { completeToast, failedToast } from './bulk-edit-toast';
import template from './increase-decrease-bid-panel-template.pug';

const i18n = globalContext.require('@bingads-webui/i18n-amd-loader!campaignui/feature/hotel-center');
const config = globalContext.require('component/config/index');
const decimal = globalContext.require('component/humanize/decimal');

/**
 * define Object State
 * @typedef {Object} State
 * @property {string} scope - enum, 'hotel' or 'hotelGroup'
 * @property {string} action - enum, 'increase' or 'decrease'
 * @property {number} amount
 * @property {string} unit - enum, 'number' or 'percent'
 * @property {number} bidCap
 * @property {boolean} isBidCapEnabled
 * @property {boolean} showRequestError
 * @property {string[]} errors
 * @property {number} selectionCount
 * @property {Object[]} selectedItems
 * @property {string} title
 * @property {string} bidType - enum, see BID_TYPE below
 * @property {Object} filter - equals to grid.filter.getCurrentFilter()
 */

const TITLE_MAP = {
  hotel: {
    singular: 'HotelCenter_Overview_Edit_Increase_Decrease_Bids_Label_Hotel',
    plural: 'HotelCenter_Overview_Edit_Increase_Decrease_Bids_Label_Hotels',
  },
  hotelGroup: {
    singular: 'HotelCenter_Overview_Edit_Increase_Decrease_Bids_Label_HotelGroup',
    plural: 'HotelCenter_Overview_Edit_Increase_Decrease_Bids_Label_HotelGroups',
  },
};

const BID_TYPE = {
  fixed: '#Model.FixedBid',
  percentage: '#Model.PercentageBid',
};

function getBitTypeSet(selectedItems) {
  return _.chain(selectedItems)
    .map(item => item.Bid['@odata.type'])
    .uniq()
    .value();
}
function initBidNumberInput(view) {
  const Entity = backboneSchema(FixedBid, i18n, ['Amount', '@odata.type']);
  const entity = new Entity();

  view.bidNumberInput = new InputControl({
    entity,
    field: 'Amount',
    ariaLabel: i18n.getString('HotelCenter_Shared_Bid_Number'),
  });
  view.bidNumberInput.on('input-changed', () => {
    view.setState({ showRequestError: false });
  });
  view.setChild('bid-number-input', view.bidNumberInput);
}

function initBidPercentInput(view) {
  const Entity = backboneSchema(PercentageBid, i18n, ['Amount', '@odata.type']);
  const entity = new Entity();

  view.bidPercentInput = new InputControl({
    entity,
    field: 'Amount',
    ariaLabel: i18n.getString('HotelCenter_Shared_Bid_Percent'),
  });
  view.bidPercentInput.on('input-changed', () => {
    view.setState({ showRequestError: false });
  });
  view.setChild('bid-percent-input', view.bidPercentInput);
}

function initBidCapNumberInput(view) {
  const Entity = backboneSchema(FixedBid, i18n, ['Amount', '@odata.type']);
  const entity = new Entity();

  view.bidCapNumberInput = new InputControl({
    entity,
    field: 'Amount',
    ariaLabel: i18n.getString('HotelCenter_Shared_Bid_Cap_Number'),
  });
  view.bidCapNumberInput.on('input-changed', () => {
    view.setState({ showRequestError: false });
  });
  view.setChild('bid-cap-number-input', view.bidCapNumberInput);
}

function initBidCapPercentInput(view) {
  const Entity = backboneSchema(PercentageBid, i18n, ['Amount', '@odata.type']);
  const entity = new Entity();

  view.bidCapPercentInput = new InputControl({
    entity,
    field: 'Amount',
    ariaLabel: i18n.getString('HotelCenter_Shared_Bid_Cap_Percent'),
  });
  view.bidCapPercentInput.on('input-changed', () => {
    view.setState({ showRequestError: false });
  });
  view.setChild('bid-cap-percent-input', view.bidCapPercentInput);
}

/**
 * Class representing a panel for bulk increase or decrease bid
 * @extends BulkEditPanelBase
 * @event "saved" event is triggered when bulk operation compeleted successfully
 * @event "canceled" event is triggered when click cancel button
 * @event "error" event is triggered when bulk operation ends with error
 * @returns {BulkIncreaseDecreaseBidPanel} - BulkIncreaseDecreaseBidPanel
 */
export default class BulkIncreaseDecreaseBidPanel extends BulkEditPanelBase {
  initialize({
    viewConfig,
    selectedRowCount,
    gridName,
  }) {
    let selectedItems = [];
    let bidTypeSet = [];
    this.mainGrid = viewConfig.grid;
    selectedItems = this.mainGrid.gridView.selectedItems();
    bidTypeSet = getBitTypeSet(selectedItems);
    this.mainGrid.gridView.on('didSelect', () => {
      this.onSelected();
    });
    this.onEvent();
    this.i18n = i18n;
    this.accountCurrency = config.get('AccountCurrencyCode');
    this.percentSymbol = decimal.getFormat().percentSymbol;
    this.BID_TYPE = BID_TYPE;
    super.initialize({
      completeToast: _.partial(completeToast, 'HotelCenter_BulkEdit_Update_Bid_Success', 'HotelCenter_BulkEdit_Update_Bid_Partial_Success'),
      failedToast: _.partial(failedToast, 'HotelCenter_BulkEdit_Update_Bid_Failed'),
      activityName: 'bulkEditIncreaseDecreaseBid',
    });
    this.scope = 'hotel';
    if (gridName === 'HotelGroupGrid') {
      this.scope = 'hotelGroup';
    }
    this.setState({
      scope: this.scope,
      action: 'increase',
      unit: 'number',
      bidType: BID_TYPE.fixed,
      isBidCapEnabled: false,
      errors: [],
    });
    this.updateSelection({
      selectionCount: selectedRowCount || 0,
      selectedItems,
      bidTypeSet,
      filter: null,
    });
  }

  getBatchAction(state) {
    const {
      action, // increase || decrease
      amount,
      unit, // number || percent
      bidCap,
      isBidCapEnabled,
    } = state;
    const actionType = _.result({
      number: 'ChangeByAmount',
      percent: 'ChangeByPercent',
    }, unit);
    const factor = _.result({
      increase: '',
      decrease: '-',
    }, action);
    const value = factor + amount;
    const limit = isBidCapEnabled ? bidCap : null;
    const batchActions = {
      Actions: [{
        '@odata.type': '#Model.NumericAction',
        ActionType: actionType,
        Limit: limit,
        Value: value,
        Property: 'Bid/Amount',
      }],
    };

    return batchActions;
  }

  onSelected = () => {
    const selectedItems = this.mainGrid.gridView.selectedItems();
    const selectionCount =
      this.mainGrid.crossPageSelectAll && this.mainGrid.crossPageSelectAll.isAllSelected ?
        this.mainGrid.crossPageSelectAll.viewModel.totalRowsTotalPageCount() :
        selectedItems.length;
    const filter = null;
    const bidTypeSet = getBitTypeSet(selectedItems);
    this.updateSelection({
      selectionCount,
      selectedItems,
      bidTypeSet,
      filter,
    });
  }
  onCancel() {
    super.onCancel();
  }

  updateSelection({
    selectionCount,
    selectedItems,
    bidTypeSet,
    filter,
  }) {
    const titleKey = TITLE_MAP[super.getState().scope][selectionCount === 1 ? 'singular' : 'plural'];
    const title = this.i18n.getString(titleKey)
      .replace('{0}', selectionCount);

    this.setState({
      selectionCount,
      selectedItems,
      filter,
      title,
      showRequestError: false,
    });
    this.bulkEditView.$('.preview-container').hide();
    this.bulkEditView.viewModel.disableAction(_.size(bidTypeSet) !== 1);
    if (_.size(bidTypeSet) === 1) {
      const bidType = _.first(bidTypeSet);

      this.setState({ bidType });
      if (bidType === BID_TYPE.percentage) {
        this.setState({ unit: 'percent' });
      }
    }
  }

  get template() {
    return template;
  }

  get events() {
    return {
      'change .action': ({ target }) => {
        this.setState({
          action: target.value,
          showRequestError: false,
        });
      },
      'change .unit': ({ target }) => {
        this.setState({
          unit: target.value,
          showRequestError: false,
        });
      },
      'change .bid-cap-enabled': ({ target }) => {
        this.setState({
          isBidCapEnabled: target.checked,
          showRequestError: false,
        });
      },
    };
  }

  get bidInput() {
    const { unit } = super.getState();

    return _.result({
      number: () => this.bidNumberInput,
      percent: () => this.bidPercentInput,
    }, unit);
  }

  get bidCapInput() {
    const { bidType } = super.getState();

    return _.result({
      [BID_TYPE.fixed]: () => this.bidCapNumberInput,
      [BID_TYPE.percentage]: () => this.bidCapPercentInput,
    }, bidType);
  }

  getState() {
    const state = super.getState();

    return _.defaults({
      amount: this.bidInput.getState().value,
      bidCap: this.bidCapInput.getState().value,
    }, state);
  }

  /**
   * set state
   * @param {State} state - see definition above
   * @returns {View} - return view itself
   */
  setState(state) {
    const oldState = super.getState();

    super.setState(state);
    const newState = super.getState();

    if (_.has(state, 'unit') && (state.unit !== oldState.unit)) {
      _.result({
        percent: () => initBidPercentInput(this),
        number: () => initBidNumberInput(this),
      }, state.unit);
    }
    if (_.has(state, 'bidType') && (state.bidType !== oldState.bidType)) {
      _.result({
        [BID_TYPE.percentage]: () => initBidCapPercentInput(this),
        [BID_TYPE.fixed]: () => initBidCapNumberInput(this),
      }, state.bidType);
      this.bidCapInput.setState({ readonly: !newState.isBidCapEnabled });
    }
    if (_.has(state, 'isBidCapEnabled')) {
      const childState = {
        readonly: !state.isBidCapEnabled,
      };

      if (state.isBidCapEnabled === false) {
        childState.value = '';
        childState.showError = false;
      }
      this.bidCapInput.setState(childState);
    }
    return this;
  }

  validate() {
    const { isBidCapEnabled } = super.getState();
    const isBidValid = this.bidInput.getState().isValid;
    const isBidCapValid = isBidCapEnabled ? this.bidCapInput.getState().isValid : true;
    const isBidNotZero = this.bidInput.getState().value !== '0';

    if (!isBidValid) {
      this.bidInput.setState({ showError: true });
    } else if (!isBidNotZero) {
      this.setState({
        showRequestError: true,
        errors: [i18n.getString('HotelCenter_Overview_Bulk_Errormsg_BidAmountIsZero')],
      });
    }
    if (!isBidCapValid) {
      this.bidCapInput.setState({ showError: true });
    }
    return isBidValid && isBidCapValid && isBidNotZero;
  }

  renderTrunk() {
    super.renderTrunk();
    this.$('.bulk-edit-container').append(this.bulkEditView.el);
  }

  remove() {
    this.bulkEditView.remove();
    super.remove();
  }
}
